import React, { useState } from 'react';
import { Card, Table, Button, Alert } from 'antd';
import { StarOutlined, HistoryOutlined, FlagOutlined } from '@ant-design/icons';
import { useUserLoyaltyTx } from '../../../hooks/useUserLoyaltyTx';
import { LoyaltyStatusCard, NotesCard } from '../../../components/user-detail';
import { formatVnDate, formatVnTime } from '../../../utils/vnDateTime';

const ROUNDED = { borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };

interface LoyaltySummary {
  currentBalance: number;
  lifetimeEarned: number;
  expiringSoon: number;
  tier: string;
  goal: number;
}

interface LoyaltyPointsTabProps {
  userId: string;
  loyaltySummary: LoyaltySummary;
  noteValue: string;
  onNoteChange: (v: string) => void;
  onSaveNote?: () => void;
}

export const LoyaltyPointsTab: React.FC<LoyaltyPointsTabProps> = ({
  userId,
  loyaltySummary,
  noteValue,
  onNoteChange,
  onSaveNote,
}) => {
  const [page, setPage] = useState(1);
  const limit = 20;

  const {
    data,
    isLoading: loading,
    isError: error,
    refetch,
  } = useUserLoyaltyTx(userId, { page, limit });

  const transactions = data?.data ?? [];
  const pagination = data?.pagination;

  const columns = [
    {
      title: 'TRANSACTION ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'DATE & TIME',
      dataIndex: 'date',
      key: 'date',
      render: (v: string) => (
        <div>
          <div>{formatVnDate(v, 'MMM D, YYYY')}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {formatVnTime(v, 'hh:mm A')}
          </div>
        </div>
      ),
    },
    {
      title: 'ACTIVITY',
      dataIndex: 'activity',
      key: 'activity',
    },
    {
      title: 'CHANGE',
      dataIndex: 'change',
      key: 'change',
      render: (v: number) => (
        <span
          style={{ color: v >= 0 ? '#16a34a' : '#dc2626', fontWeight: 500 }}
        >
          {v >= 0 ? '+' : ''}
          {v} pts
        </span>
      ),
    },
    {
      title: 'BALANCE',
      dataIndex: 'balance',
      key: 'balance',
      render: (v: number) => `${v.toLocaleString()} pts`,
    },
  ];

  if (error) {
    return (
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}
      >
        <Alert
          type="error"
          message="Không thể tải lịch sử điểm thưởng."
          showIcon
          action={
            <Button size="small" onClick={() => refetch()}>
              Thử lại
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 24,
          }}
        >
          <Card style={ROUNDED} bodyStyle={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: '#ffedd5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <StarOutlined style={{ fontSize: 20, color: '#ea580c' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  Current Balance
                </div>
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}
                >
                  {loyaltySummary.currentBalance.toLocaleString()} pts
                </div>
              </div>
            </div>
          </Card>
          <Card style={ROUNDED} bodyStyle={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <HistoryOutlined style={{ fontSize: 20, color: '#2563eb' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  Lifetime Earned
                </div>
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}
                >
                  {loyaltySummary.lifetimeEarned.toLocaleString()} pts
                </div>
              </div>
            </div>
          </Card>
          <Card style={ROUNDED} bodyStyle={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: '#fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FlagOutlined style={{ fontSize: 20, color: '#dc2626' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  Expiring Soon
                </div>
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: '#dc2626' }}
                >
                  {loyaltySummary.expiringSoon} pts
                </div>
              </div>
            </div>
          </Card>
        </div>

        <Card title="Points Transaction History" style={ROUNDED}>
          <Table
            loading={loading}
            dataSource={transactions}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: limit,
              total: pagination?.total ?? 0,
              onChange: setPage,
              showSizeChanger: false,
            }}
            size="middle"
            columns={columns}
            locale={{ emptyText: 'No transactions' }}
          />
        </Card>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <LoyaltyStatusCard
          tier={loyaltySummary.tier}
          currentPoints={loyaltySummary.currentBalance}
          goal={loyaltySummary.goal}
        />
        <Card style={ROUNDED}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Current Balance</span>
              <span style={{ fontWeight: 600 }}>
                {loyaltySummary.currentBalance.toLocaleString()} pts
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Lifetime Earned</span>
              <span style={{ fontWeight: 600 }}>
                {loyaltySummary.lifetimeEarned.toLocaleString()} pts
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Expiring Soon</span>
              <span style={{ fontWeight: 600, color: '#dc2626' }}>
                {loyaltySummary.expiringSoon} pts
              </span>
            </div>
            <Button type="default" style={{ marginTop: 8, borderRadius: 8 }}>
              View Tier Benefits
            </Button>
          </div>
        </Card>
        <NotesCard
          value={noteValue}
          onChange={onNoteChange}
          onSave={onSaveNote}
          showSaveButton={false}
        />
      </div>
    </div>
  );
};
